import Head from 'next/head';
import { useRouter } from 'next/router';

import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { sneakersRequest } from 'server/services/sneakers/sneakersRequest';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { FUTURE, PAST_YEAR, PRESENT } from 'timeline/enums/constants';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { usePageMetadataCS } from 'shared/hooks/usePageMetadataCS';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import ProductSearchGrid from 'shared/components/product-grid';
import { useCallback } from 'react';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { setIsHeaderVisible } from 'store/slice/pageLayoutSlice';
import {
  getPresentTimelineYear,
  getTimeStamps,
  getUpcomingRange,
} from 'shared/components/product-grid/utils/gridUtils';

const TP = 'pages.timeline';

const TimelineYear = ({
  constructorResponse,
  globalPricingId,
  timelineBreadcrumbs,
  isCrawler = false,
}) => {
  const router = useRouter();
  const { query } = router;
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);
  const storeDispatch = useStoreDispatch();
  const toggleHeader = useCallback((status) => {
    storeDispatch(setIsHeaderVisible(status));
  }, []);

  const breadcrumb = timelineBreadcrumbs?.find(
    (yearBreadcrumb) => yearBreadcrumb.name === query?.timelineYear?.[0],
  );

  const metadataCS = usePageMetadataCS({});
  const metadataLegacy = usePageMetadata({
    defaultTitle: t(`${TP}.title`, 'Sneaker Release Calendar'),
    constructorResponse,
    mainTitle: breadcrumb?.titleTag,
    mainDescription: breadcrumb?.metadataDescription,
  });

  const { pageTitle: titleWithCountryCode, pageDescription } = isNewSearchPageEnabled
    ? metadataCS
    : metadataLegacy;

  const metadataFutureCS = usePageMetadataCS({});
  const metadataFutureLegacy = usePageMetadata({
    defaultTitle: t(`${TP}.futureTitle`, 'Upcoming Sneaker Releases'),
    constructorResponse,
  });

  const { pageTitle: futureTitleWithCountryCode, pageDescription: futurePageDescription } =
    isNewSearchPageEnabled ? metadataFutureCS : metadataFutureLegacy;

  const isFuture = query?.timelineYear?.[0] === FUTURE;
  const isPresent = router?.asPath?.split('?')?.[0] === '/timeline';

  const timelineYear = router?.query?.timelineYear;
  const isFutureOrPresent =
    timelineYear?.[0] === FUTURE || router?.asPath?.split('?')?.[0] === '/timeline';

  const allPageFilters = useCallback(() => {
    const timelineYears = router?.query?.timelineYear;

    const isFutureOrPresents =
      timelineYears?.[0] === FUTURE || router?.asPath?.split('?')?.[0] === '/timeline';
    let releaseDateStart = 0;
    let releaseDateEnd = 0;

    if (isFuture) {
      releaseDateStart = getTimeStamps(getUpcomingRange().start);
      releaseDateEnd = getTimeStamps(getUpcomingRange().end);
    } else if (isPresent) {
      releaseDateStart = getTimeStamps(getPresentTimelineYear().start);
      releaseDateEnd = getTimeStamps(getPresentTimelineYear().end);
    }

    return {
      categories: 'footwear',
      productTypes: 'sneakers',
      releaseDateStart,
      releaseDateEnd,
      releaseYears: isFutureOrPresents ? '' : timelineYears?.[0],
    };
  }, [router]);

  return (
    <>
      <Head>
        {isFuture ? (
          <>
            <title>{futureTitleWithCountryCode}</title>
            <meta
              name="description"
              content={
                futurePageDescription ||
                t(
                  `${TP}.futureDescription`,
                  'Stay up to date on the latest sneaker drops and upcoming sneaker releases from Air Jordan, Yeezy, Nike, and more.',
                )
              }
              key="description"
            />
          </>
        ) : (
          <>
            <title>{titleWithCountryCode}</title>
            <meta
              name="description"
              content={
                pageDescription ||
                t(
                  `${TP}.description`,
                  'Shop new sneaker releases and iconic styles from Air Jordan, Nike, Yeezy and more on GOAT, the global platform for the greatest products from the past, present and future.',
                )
              }
              key="description"
            />
          </>
        )}
      </Head>
      <ProductSearchGrid
        collectionSlug=""
        isCrawler={isCrawler}
        globalPricingId={globalPricingId}
        toggleHeader={toggleHeader}
        pageFilters={allPageFilters()}
        pageAttributes={{
          timelineBreadcrumbs,
          isTimelinePath: true,
          timelineType: isFuture ? FUTURE : isPresent ? PRESENT : PAST_YEAR,
          timelineYear: isFuture
            ? '2025'
            : isFutureOrPresent
              ? ''
              : router?.query?.timelineYear?.[0],
        }}
      />
    </>
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const pathname = resolvedUrl?.split('?')?.[0];
  const { timelineYear }: { timelineYear: string } = query;
  const isCrawler = isSearchCrawler(req?.headers);

  if (!timelineYear && pathname !== '/timeline') {
    return { notFound: true };
  }

  try {
    // future and present are not using constructor collection, and is just a 'release_date' filter

    const globalPricingId = await fetchGlobalPricingId({ req, res });

    let breadcrumbs = {};

    try {
      const breadcrumbsResponse: string | ICMSBreadcrumb = await sneakersRequest(req, res)
        .get(`api/v1/breadcrumbs/${BREADCRUMBS.TIMELINE}/years?includeChildren=true`)
        .then((response) => response.body);

      if (typeof breadcrumbsResponse !== 'string') {
        breadcrumbs = breadcrumbsResponse?.children;
      }
    } catch (error) {
      bugsnag(error, { context: 'Error fetching Timeline breadcrumbs' });
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        constructorResponse: {},
        globalPricingId,
        timelineBreadcrumbs: breadcrumbs,
        isCrawler,
      },
    };
  } catch {
    return { notFound: true };
  }
};

export default TimelineYear;
